// Renders a lesson's body blocks (see catalog/types.ts LessonBlock). There is
// no markdown: paragraphs, headings, numbered steps, bullets, and callouts,
// with **bold** runs inside any text (BOLD_RUN), which lessons use for the
// exact UI label the reader should look for.
import { ReactNode } from "react";
import cn from "classnames";
import Callout from "../../components/common/Callout";
import { createRevealSequence } from "../../components/common/Reveal";
import { BOLD_RUN, LessonBlock } from "../catalog/types";

/** Splits "Click **Upload**" into text and <b> runs. */
export function renderRuns(text: string): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  const re = new RegExp(BOLD_RUN.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <b key={i++} className="font-semibold text-base-content">
        {m[1]}
      </b>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  // Plain strings need no keys; a single run is returned as-is.
  if (out.length === 0) return text;
  return out.length === 1 ? out[0] : out;
}

type Enter = () => { className: string; style: React.CSSProperties };

export const headingId = (text: string) =>
  "h-" +
  text
    .replace(/\*\*/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** The body's headings as anchor targets (for an "On this page" list). */
export const headingAnchors = (blocks: LessonBlock[]) =>
  blocks.filter((b): b is Extract<LessonBlock, { type: "heading" }> => b.type === "heading").map((b) => ({ id: headingId(b.text), text: b.text.replace(/\*\*/g, "") }));

const LessonBody: React.FC<{ blocks: LessonBlock[]; className?: string; reveal?: Enter; withAnchors?: boolean }> = ({ blocks, className, reveal, withAnchors }) => {
  const enter: Enter = reveal ?? createRevealSequence();
  return (
    <div className={className} data-testid="lesson-body">
      {blocks.map((block, i) => {
        const r = enter();
        switch (block.type) {
          case "heading":
            return (
              <h2 key={i} id={withAnchors ? headingId(block.text) : undefined} style={r.style} className={cn(r.className, "text-lg font-semibold text-base-content mt-8 mb-2 first:mt-0 scroll-mt-20")}>
                {renderRuns(block.text)}
              </h2>
            );
          case "paragraph":
            return (
              <p key={i} style={r.style} className={cn(r.className, "text-sm leading-relaxed text-base-content/80 my-3")}>
                {renderRuns(block.text)}
              </p>
            );
          case "steps":
            return (
              <ol key={i} style={r.style} className={cn(r.className, "my-4 space-y-2 pl-0 list-none")}>
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-3 text-sm leading-relaxed text-base-content/80">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-[11px] font-semibold tabular-nums">
                      {j + 1}
                    </span>
                    <span className="min-w-0">{renderRuns(item)}</span>
                  </li>
                ))}
              </ol>
            );
          case "bullets":
            return (
              <ul key={i} style={r.style} className={cn(r.className, "my-4 space-y-1.5 pl-5 list-disc marker:text-blue-500 text-sm leading-relaxed text-base-content/80")}>
                {block.items.map((item, j) => (
                  <li key={j}>{renderRuns(item)}</li>
                ))}
              </ul>
            );
          case "callout":
            return (
              <div key={i} style={r.style} className={cn(r.className, "my-4")}>
                <Callout tone={block.tone} title={block.title}>
                  <p>{renderRuns(block.text)}</p>
                </Callout>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default LessonBody;
